/**
 * chartAnnotations.js
 * Fonctions pures de construction des annotations Chart.js pour PMChart
 * Extrait de PMChart.jsx pour respecter la limite 500 lignes
 * Retourne les objets annotation compatibles chartjs-plugin-annotation
 * 
 * Seuils adaptatifs : un seuil n'est affiché que si les données
 * atteignent au moins 50% de sa valeur (évite d'écraser le graphique)
 */

import { PM_THRESHOLDS } from '../constants/pmThresholds';
import { resolveAnnotationLabel } from '../constants/activityTypes';
import { formatDateLocale } from '../utils/formatters';

// Alias rétrocompatible — les composants qui importent THRESHOLDS continuent de fonctionner
export const THRESHOLDS = PM_THRESHOLDS;

// Durée d'une annotation d'activité dont l'heure de fin ne tombe sur aucune mesure.
const FALLBACK_BOX_MS = 5 * 60000;

/**
 * Couleurs de fond par zone de seuil PM2.5
 * @param {number} yAxisMax - valeur max de l'axe Y
 * @param {boolean} darkMode
 * @param {number} [maxMeasured] - valeur max mesurée (PM2.5). Si fournie, filtre les zones inutiles.
 */
export function buildBackgroundAnnotations(yAxisMax, darkMode, maxMeasured) {
  // 3 zones D2, alignées sur les badges : ≤ warningMax (bon) / ≤ alertMax (à surveiller) / > alertMax (élevé).
  const { warningMax, alertMax } = THRESHOLDS.pm25;
  const zones = [
    { yMin: 0,          yMax: warningMax, colorLight: 'rgba(77, 166, 255, 0.08)', colorDark: 'rgba(144,202,249,0.05)', threshold: 0 },
    { yMin: warningMax, yMax: alertMax,   colorLight: 'rgba(239, 159, 39, 0.08)', colorDark: 'rgba(239, 159, 39, 0.05)', threshold: warningMax },
    { yMin: alertMax,   yMax: yAxisMax,   colorLight: 'rgba(191, 49, 26, 0.08)',  colorDark: 'rgba(191, 49, 26, 0.05)', threshold: alertMax }
  ];

  return zones
    .filter(z => !maxMeasured || maxMeasured >= z.threshold * 0.5)
    .map(z => ({
      type: 'box',
      yMin: z.yMin,
      yMax: z.yMax,
      backgroundColor: darkMode ? z.colorDark : z.colorLight,
      borderWidth: 0
    }));
}

/**
 * Lignes horizontales de seuil PM2.5
 * Labels positionnés à droite pour ne pas masquer les courbes
 * @param {boolean} darkMode
 * @param {number} [maxMeasured] - valeur max mesurée (PM2.5). Si fournie, masque les seuils lointains.
 */
export function buildThresholdLines(darkMode, maxMeasured, t) {
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 900;
  // 2 lignes D2 (warningMax / alertMax), vocabulaire « À surveiller » / « Élevé »,
  // cohérent badges + spec §13.3.4. yAdjust décale le label sous la ligne.
  // i18n (bascule E1) : libellés via `t` (réutilise alerts.level.watch/high, mêmes
  // chaînes FR byte-for-byte). Fallback FR gelé si `t` absent (compat tests/anciens appels).
  const watchLabel = t ? t('alerts.level.watch') : 'À surveiller';
  const highLabel  = t ? t('alerts.level.high')  : 'Élevé';
  const { warningMax, alertMax } = THRESHOLDS.pm25;
  const lines = [
    { yVal: warningMax, labelFull: `${watchLabel} (${warningMax} µg/m³)`, labelShort: `${warningMax}`, cLight: 'rgba(239, 159, 39, 0.8)', cDark: 'rgba(239, 159, 39, 0.6)', yAdj: 12 },
    { yVal: alertMax,   labelFull: `${highLabel} (${alertMax} µg/m³)`,     labelShort: `${alertMax}`,   cLight: 'rgba(191, 49, 26, 0.8)', cDark: 'rgba(191, 49, 26, 0.6)', yAdj: 12 }
  ];

  return lines
    .filter(l => !maxMeasured || maxMeasured >= l.yVal * 0.5)
    .map(l => ({
      type: 'line',
      yMin: l.yVal,
      yMax: l.yVal,
      borderColor: darkMode ? l.cDark : l.cLight,
      borderWidth: 1.5,
      borderDash: [6, 4],
      label: {
        display: true,
        content: isMobile ? l.labelShort : l.labelFull,
        position: 'start',       // Côté gauche — ne masque pas les données à droite
        xAdjust: 6,              // Petit espace depuis le bord gauche
        yAdjust: l.yAdj,         // Décaler sous la ligne pour éviter le chevauchement
        backgroundColor: darkMode ? l.cDark.replace('0.6', '0.9') : l.cLight.replace('0.8', '1'),
        color: '#fff',
        font: { size: isMobile ? 9 : 10, weight: '600' },
        padding: isMobile ? 2 : 4,
        borderRadius: 3,
      }
    }));
}

const ACTIVITY_COLORS = {
  box_cleaning: { bg: 'rgba(255, 193, 7, 0.15)',  border: 'rgba(255, 193, 7, 0.6)' },
  hay_dry:      { bg: 'rgba(76, 175, 80, 0.15)',   border: 'rgba(76, 175, 80, 0.6)' },
  hay_soaked:   { bg: 'rgba(33, 150, 243, 0.15)',  border: 'rgba(33, 150, 243, 0.6)' },
  bedding:      { bg: 'rgba(156, 39, 176, 0.15)',  border: 'rgba(156, 39, 176, 0.6)' },
  mucking:      { bg: 'rgba(233, 30, 99, 0.15)',   border: 'rgba(233, 30, 99, 0.6)' },
  grooming:     { bg: 'rgba(0, 188, 212, 0.15)',   border: 'rgba(0, 188, 212, 0.6)' },
  paddock:      { bg: 'rgba(139, 195, 74, 0.15)',  border: 'rgba(139, 195, 74, 0.6)' },
  ride:         { bg: 'rgba(255, 87, 34, 0.15)',   border: 'rgba(255, 87, 34, 0.6)' },
  other:        { bg: 'rgba(158, 158, 158, 0.15)', border: 'rgba(158, 158, 158, 0.6)' }
};

// Zones colorées pour les activités annotées par l'utilisateur.
// `t` (react-i18next) résout le libellé depuis ann.type (bascule i18n E1) ;
// si absent, fallback sur ann.label gelé (compat tests / anciennes données).
export function buildActivityAnnotations(annotations, readings, yAxisMax, darkMode, t) {
  if (!annotations?.length || !readings?.length) return [];

  const sessionDate = readings[0]?.timestamp?.split('T')[0];

  return annotations.map((ann, idx) => {
    const startStr = ann.startTime ? `${sessionDate}T${ann.startTime}:00` : ann.timestamp;
    const endStr   = ann.endTime   ? `${sessionDate}T${ann.endTime}:00`   : ann.timestamp;
    if (!startStr) return null;

    const annStart = new Date(startStr);
    const annEnd   = new Date(endStr);
    const TOLERANCE = 60000; // 1 minute

    const startIdx = readings.findIndex(r => Math.abs(new Date(r.timestamp) - annStart) < TOLERANCE);
    const endIdx   = readings.findIndex(r => Math.abs(new Date(r.timestamp) - annEnd) < TOLERANCE);

    if (startIdx === -1) return null;
    // Fin introuvable : boîte de 5 min, comptée sur les horodatages et non en
    // points (5 points ne font que 5 s au pas de 1 s).
    const fallbackEnd = annStart.getTime() + FALLBACK_BOX_MS;
    const finalEndIdx = endIdx !== -1 ? endIdx : readings.reduce(
      (last, r, i) => (i > startIdx && new Date(r.timestamp).getTime() <= fallbackEnd ? i : last), startIdx);

    const colors = ACTIVITY_COLORS[ann.type] || ACTIVITY_COLORS.other;

    return {
      type: 'box',
      xMin: startIdx,
      xMax: finalEndIdx,
      yMin: 0,
      yMax: yAxisMax,
      backgroundColor: darkMode ? colors.bg.replace('0.15', '0.1') : colors.bg,
      borderColor: colors.border,
      borderWidth: 2,
      borderDash: [5, 3],
      label: {
        display: true,
        content: resolveAnnotationLabel(ann, t),
        position: 'start',
        backgroundColor: colors.border,
        color: '#FFFFFF',
        font: { size: 10, weight: '600' },
        padding: 4,
        yAdjust: -10
      }
    };
  }).filter(Boolean);
}

// Format label démarcation jour : FR « lun. 17/05 » / EN « Mon 17/05 » (jour abrégé
// localisé + DD/MM numérique en-GB, sans année). Le jour abrégé passe par formatDateLocale
// ({weekday:'short'}) : FR rend « lun. » (byte-for-byte l'ancien rendu normalisé),
// EN rend « Mon ». DD/MM reste numérique (en-GB natif) pour un marqueur d'axe compact.
// Exporté pour les tests.
export function formatDayLabel(date) {
  const d = date instanceof Date ? date : new Date(date);
  if (isNaN(d.getTime())) return '';
  const weekday = formatDateLocale(d, { weekday: 'short' });
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  return `${weekday} ${dd}/${mm}`;
}

/**
 * Démarcation visuelle des frontières de jour sur un axe X catégoriel
 * (readings indexés 0..n-1). Affichée uniquement si la plage couverte par
 * readings comprend ≥ 2 jours distincts (toDateString locale).
 *
 * Algorithme O(n) : parcours readings, pour i ≥ 1 si jour(i) ≠ jour(i-1),
 * créer une ligne verticale fine à xMin=xMax=i avec label "lun. DD/MM".
 *
 * @param {Array<{timestamp: string|Date}>} readings - ordre chrono ascendant
 * @param {boolean} darkMode
 * @returns {Array} annotations Chart.js (vide si <2 jours distincts)
 */
export function buildDayBoundaryAnnotations(readings, darkMode) {
  if (!Array.isArray(readings) || readings.length < 2) return [];

  const dayKey = (r) => {
    const ts = r?.timestamp;
    if (!ts) return null;
    const d = ts instanceof Date ? ts : new Date(ts);
    return isNaN(d.getTime()) ? null : d.toDateString();
  };

  // Plage < 24h (1 seul jour distinct) → aucune annotation
  const distinctDays = new Set(readings.map(dayKey).filter(Boolean));
  if (distinctDays.size < 2) return [];

  const borderColor = darkMode ? 'rgba(255,255,255,0.20)' : 'rgba(0,0,0,0.18)';
  const labelColor  = darkMode ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.6)';

  const out = [];
  for (let i = 1; i < readings.length; i++) {
    const cur = dayKey(readings[i]);
    const prev = dayKey(readings[i - 1]);
    if (cur && prev && cur !== prev) {
      out.push({
        type: 'line',
        xMin: i,
        xMax: i,
        borderColor,
        borderWidth: 1,
        borderDash: [4, 4],
        label: {
          display: true,
          content: formatDayLabel(new Date(readings[i].timestamp)),
          position: 'start',
          backgroundColor: 'transparent',
          color: labelColor,
          font: { size: 10 },
          yAdjust: -4,
        },
      });
    }
  }
  return out;
}
